/**
 * Document-level color theme application.
 *
 * Palettes and pure helpers live in utils/color-theme.ts. This module owns
 * `data-theme`, `color-scheme`, `data-darken-puzzle-colors`, the theme-color
 * meta, and change events.
 */
import type { GameOptions } from '../services/options.js';
import {
  CHROME_THEMES,
  resolveColorTheme,
  shouldDarkenPuzzleColors,
  themePuzzleColors,
  themedCellCrossHex,
  withThemedCellCrosses,
  THEME_COLOR_META,
  type ChromeTheme,
  type ColorThemePreference,
  type ResolvedColorTheme,
} from '../../utils/color-theme.js';

export {
  CHROME_THEMES,
  DARK_PALETTE_DARKEN_AMOUNT,
  resolveColorTheme,
  shouldDarkenPuzzleColors,
  themePuzzleColors,
  themedCellCrossHex,
  withThemedCellCrosses,
  THEME_COLOR_META,
} from '../../utils/color-theme.js';
export type {
  ChromeTheme,
  ColorThemePreference,
  ResolvedColorTheme,
} from '../../utils/color-theme.js';

export const COLOR_THEME_CHANGE_EVENT = 'nono-color-theme-change';

export interface ColorThemeChangeDetail {
  theme: ResolvedColorTheme;
  darkenPuzzleColors: boolean;
}

export function systemPrefersDark(
  media: Pick<MediaQueryList, 'matches'> | null = defaultColorSchemeMedia(),
): boolean {
  return media?.matches ?? false;
}

export function getResolvedColorTheme(
  root: HTMLElement = document.documentElement,
): ResolvedColorTheme {
  return root.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function getDarkenPuzzleColors(
  root: HTMLElement = document.documentElement,
): boolean {
  return root.dataset.darkenPuzzleColors === 'true';
}

export function getChromeTheme(theme?: ResolvedColorTheme): ChromeTheme {
  const resolved = theme ?? getResolvedColorTheme();
  return withThemedCellCrosses(CHROME_THEMES[resolved], resolved, getDarkenPuzzleColors());
}

export function themePuzzleColorsForDocument(colors: string[]): string[] {
  return themePuzzleColors(colors, getResolvedColorTheme(), getDarkenPuzzleColors());
}

export function themedCellCrossHexForDocument(): string {
  return themedCellCrossHex(getResolvedColorTheme(), getDarkenPuzzleColors());
}

export function applyResolvedColorTheme(
  theme: ResolvedColorTheme,
  root: HTMLElement = document.documentElement,
  darkenPuzzleColors = getDarkenPuzzleColors(root),
): void {
  const alreadyApplied =
    root.dataset.theme === theme &&
    root.style.colorScheme === theme &&
    getDarkenPuzzleColors(root) === darkenPuzzleColors;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  root.dataset.darkenPuzzleColors = darkenPuzzleColors ? 'true' : 'false';
  const meta = root.ownerDocument?.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR_META[theme]);
  if (alreadyApplied) return;
  root.ownerDocument?.defaultView?.dispatchEvent(
    new CustomEvent<ColorThemeChangeDetail>(COLOR_THEME_CHANGE_EVENT, {
      detail: { theme, darkenPuzzleColors },
    }),
  );
}

export function subscribeColorThemeChange(
  listener: (theme: ResolvedColorTheme) => void,
): () => void {
  const handler = (event: Event) => {
    const theme = (event as CustomEvent<ColorThemeChangeDetail>).detail?.theme
      ?? getResolvedColorTheme();
    listener(theme);
  };
  window.addEventListener(COLOR_THEME_CHANGE_EVENT, handler);
  return () => window.removeEventListener(COLOR_THEME_CHANGE_EVENT, handler);
}

let installedPreference: ColorThemePreference = 'light';
let installedDarkenPuzzleColors = false;
let mediaQuery: MediaQueryList | null = null;
let mediaHandler: (() => void) | null = null;

export function installColorTheme(
  preference: ColorThemePreference,
  darkenPuzzleColors = false,
): ResolvedColorTheme {
  installedPreference = preference;
  installedDarkenPuzzleColors = darkenPuzzleColors;
  const resolved = resolveColorTheme(preference, systemPrefersDark());
  applyResolvedColorTheme(
    resolved,
    document.documentElement,
    shouldDarkenPuzzleColors(resolved, darkenPuzzleColors),
  );
  listenForSystemThemeChanges();
  return resolved;
}

export function applyColorThemeFromOptions(
  options: Pick<GameOptions, 'colorTheme' | 'darkenPuzzleColors'>,
): ResolvedColorTheme {
  return installColorTheme(options.colorTheme, options.darkenPuzzleColors);
}

/** Apply every option that lives on the document: the color theme and reduced motion. */
export function applyDocumentOptions(
  options: Pick<GameOptions, 'colorTheme' | 'darkenPuzzleColors' | 'reducedMotion'>,
): ResolvedColorTheme {
  applyReducedMotion(options);
  return applyColorThemeFromOptions(options);
}

/**
 * Marks the document with the game's reduced-motion setting (not the system
 * preference, which only seeds the setting); theme.css then drops the UI's
 * transitions.
 */
export function applyReducedMotion(options: Pick<GameOptions, 'reducedMotion'>): void {
  document.documentElement.toggleAttribute('data-reduced-motion', options.reducedMotion);
}

export function resetColorThemeForTests(): void {
  if (mediaQuery && mediaHandler) {
    mediaQuery.removeEventListener('change', mediaHandler);
  }
  mediaQuery = null;
  mediaHandler = null;
  installedPreference = 'light';
  installedDarkenPuzzleColors = false;
}

function defaultColorSchemeMedia(): MediaQueryList | null {
  return typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
}

function listenForSystemThemeChanges(): void {
  if (mediaQuery || typeof matchMedia !== 'function') return;
  mediaQuery = matchMedia('(prefers-color-scheme: dark)');
  mediaHandler = () => {
    if (installedPreference !== 'system') return;
    const resolved = resolveColorTheme('system', mediaQuery!.matches);
    applyResolvedColorTheme(
      resolved,
      document.documentElement,
      shouldDarkenPuzzleColors(resolved, installedDarkenPuzzleColors),
    );
  };
  mediaQuery.addEventListener('change', mediaHandler);
}
